import styles from "../../ConversionAndPdpViewer.module.css";
import {
  formatCurrency,
  formatCurrencyPrecise,
  formatDelta,
  formatFractionPct,
  formatNumber,
  valueToneClass,
} from "./formatters";
import { RankingTabs } from "./ui";

export default function PdpRankingsTab({
  data,
  activeRankingTab,
  onRankingTabChange,
  onOpenProduct,
  currency = "USD",
  gscEnrichment = {},
}) {
  const rawRows = data?.[activeRankingTab] || [];
  const activeRows = activeRankingTab === "bestConverting"
    ? rawRows.filter((r) => (r.conversionQuality?.pdpToPurchaseRate ?? r.purchaseToViewRate ?? 0) > 0 && (r.commercialOutcome?.revenue ?? r.itemRevenue ?? 0) > 0)
    : rawRows;
  const emptyColSpan =
    activeRankingTab === "mostOpportunity"
      ? 3
      : activeRankingTab === "bestConverting"
        ? 4
        : activeRankingTab === "losingMomentum"
          ? 6
          : activeRankingTab === "topSelling"
          ? 6
          : 5;
  const renderContributionCell = (row) => {
    const tooltip = "contribution before marketing";
    if (row.commercialOutcome?.contribution != null) {
      return (
        <span title={tooltip}>
          {formatCurrency(row.commercialOutcome.contribution, currency)}
        </span>
      );
    }
    if (row.contribution != null) {
      return (
        <span title={tooltip}>
          {formatCurrency(row.contribution, currency)}
        </span>
      );
    }
    if (row.commercialOutcome?.contributionStatus === "missing_cost_data") {
      return "Cost data missing";
    }
    return "—";
  };
  const renderMarginCell = (row) => {
    const contributionMissing =
      row.commercialOutcome?.contributionStatus === "missing_cost_data" ||
      ((row.commercialOutcome?.contribution ?? row.contribution) == null);
    if (contributionMissing) return "—";
    if (row.commercialOutcome?.marginPct != null) {
      return `${(row.commercialOutcome.marginPct * 100).toFixed(1)}%`;
    }
    if (row.marginPct != null) {
      return `${(row.marginPct * 100).toFixed(1)}%`;
    }
    return "—";
  };

  return (
    <div className={styles.sectionStack}>
      <RankingTabs activeTab={activeRankingTab} onChange={onRankingTabChange} />

      {/* {data?.aiOverview ? (
        <p className={styles.descriptionText}>{data.aiOverview}</p>
      ) : null} */}

      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead>
            {activeRankingTab === "topSelling" && (
              <tr>
                <th>Product</th>
                <th className={styles.alignRight}>Revenue</th>
                <th className={styles.alignRight}>Items sold</th>
                <th className={styles.alignRight}>PDP views</th>
                <th className={styles.alignRight}>Conv.</th>
                <th className={styles.alignRight}>
                  <span title="contribution before marketing">Contribution</span>
                </th>
              </tr>
            )}
            {activeRankingTab === "highestContribution" && (
              <tr>
                <th>Product</th>
                <th className={styles.alignRight}>Contribution</th>
                <th className={styles.alignRight}>Margin %</th>
                <th className={styles.alignRight}>Revenue</th>
                <th className={styles.alignRight}>Items sold</th>
              </tr>
            )}
            {activeRankingTab === "mostOpportunity" && (
              <tr>
                <th>Product</th>
                <th className={styles.alignRight}>PDP views</th>
                <th className={styles.alignRight}>ATC rate</th>
              </tr>
            )}
            {activeRankingTab === "bestConverting" && (
              <tr>
                <th>Product</th>
                <th className={styles.alignRight}>PDP to purchase</th>
                <th className={styles.alignRight}>Revenue/session</th>
                <th className={styles.alignRight}>Contrib./session</th>
              </tr>
            )}
            {activeRankingTab === "losingMomentum" && (
              <tr>
                <th>Product</th>
                <th className={styles.alignRight}>Impressions</th>
                <th className={styles.alignRight}>Clicks</th>
                <th className={styles.alignRight}>CTR</th>
                <th className={styles.alignRight}>Position</th>
                <th>Signal</th>
              </tr>
            )}
          </thead>
          <tbody>
            {activeRows.length === 0 ? (
              <tr>
                <td colSpan={emptyColSpan} style={{ textAlign: "center", color: "var(--tm)", padding: "1.5rem" }}>
                  No data available for this ranking.
                </td>
              </tr>
            ) : (
              activeRows.map((row, index) => {
                const productId = String(row.shopifyProductId || row.productId || row.itemId || row.id || "");
                return (
                  <tr
                    key={productId || index}
                    className={styles.clickableRow}
                    onClick={() => onOpenProduct(row)}
                  >
                    <td>{row.title || row.itemName || row.name || "—"}</td>

                    {activeRankingTab === "topSelling" && (
                      <>
                        <td className={styles.alignRight}>{formatCurrency(row.commercialOutcome?.revenue ?? row.itemRevenue, currency)}</td>
                        <td className={styles.alignRight}>{formatNumber(row.commercialOutcome?.unitsSold ?? row.itemsCheckedOut)}</td>
                        <td className={styles.alignRight}>{formatNumber(row.waterfall?.pdpViews ?? row.itemViewEvents)}</td>
                        <td className={styles.alignRight}>
                          {row.conversionQuality?.bundleBehavior?.detected
                            ? <span title={row.conversionQuality.bundleBehavior.note || "Bundle/cross-sell purchase — no PDP visit"}>Bundle *</span>
                            : formatFractionPct(row.conversionQuality?.pdpToPurchaseRate ?? row.purchaseToViewRate)
                          }
                        </td>
                        <td className={styles.alignRight}>{renderContributionCell(row)}</td>
                      </>
                    )}

                    {activeRankingTab === "highestContribution" && (
                      <>
                        <td className={styles.alignRight}>{renderContributionCell(row)}</td>
                        <td className={styles.alignRight}>{renderMarginCell(row)}</td>
                        <td className={styles.alignRight}>{formatCurrency(row.commercialOutcome?.revenue ?? row.itemRevenue, currency)}</td>
                        <td className={styles.alignRight}>{formatNumber(row.commercialOutcome?.unitsSold ?? row.itemsCheckedOut)}</td>
                      </>
                    )}

                    {activeRankingTab === "mostOpportunity" && (
                      <>
                        <td className={styles.alignRight}>{formatNumber(row.waterfall?.pdpViews ?? row.itemViewEvents)}</td>
                        <td className={styles.alignRight}>{formatFractionPct(row.conversionQuality?.pdpToAtcRate ?? row.cartToViewRate)}</td>
                      </>
                    )}

                    {activeRankingTab === "bestConverting" && (
                      <>
                        <td className={styles.alignRight}>{formatFractionPct(row.conversionQuality?.pdpToPurchaseRate ?? row.purchaseToViewRate)}</td>
                        <td className={styles.alignRight}>{formatCurrencyPrecise(row.commercialOutcome?.revenuePerSession ?? row.revenuePerView, currency)}</td>
                        <td className={styles.alignRight}>{row.commercialOutcome?.contributionPerSession != null ? formatCurrencyPrecise(row.commercialOutcome.contributionPerSession, currency) : row.contributionPerView != null ? formatCurrencyPrecise(row.contributionPerView, currency) : "—"}</td>
                      </>
                    )}

                    {activeRankingTab === "losingMomentum" && (() => {
                      const gsc = row.gscData || gscEnrichment[row.shopifyProductId] || null;
                      return (
                        <>
                          <td className={styles.alignRight}>
                            {gsc?.impressions != null ? formatNumber(gsc.impressions) : "—"}
                          </td>
                          <td className={styles.alignRight}>
                            {gsc?.clicks != null ? formatNumber(gsc.clicks) : "—"}
                          </td>
                          <td className={styles.alignRight}>
                            {gsc?.ctr_pct != null ? `${Number(gsc.ctr_pct).toFixed(1)}%` : "—"}
                          </td>
                          <td className={styles.alignRight}>
                            {gsc?.position != null ? Number(gsc.position).toFixed(1) : "—"}
                          </td>
                          <td>{row.signalLabel || "Monitor"}</td>
                        </>
                      );
                    })()}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
